import React, { useEffect, useState, useContext } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserPen, faSave } from '@fortawesome/free-solid-svg-icons'
import CustomLoader from '../layouts/CustomLoader'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface Role      { id: number; name: string }
interface PartnerOpt { id: number; name: string }
interface MktOpt    { id: number; name: string }

interface UserData {
  email      : string
  PartnerId  : number | null
  MarketingId: number | null
  Roles      : { id: number; name: string }[]
}

interface FormValues {
  email          : string
  password       : string
  passwordConfirm: string
  role           : number
  partner        : number
  marketing      : number
}

const formSchema = Yup.object().shape({
  email: Yup.string().email().required(),
  password: Yup.string()
    .min(8, 'Must be 8 characters or more')
    .matches(/[a-z]+/, 'One lowercase character')
    .matches(/[A-Z]+/, 'One uppercase character')
    .matches(/[@$!%*#?&.¡,+¿()]+/, 'One special character')
    .matches(/\d+/, 'One number'),
  passwordConfirm: Yup.string().oneOf([Yup.ref('password')], 'Passwords must match'),
  role           : Yup.number().min(1, 'Select a role').required(),
  partner        : Yup.number(),
  marketing      : Yup.number(),
})

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </label>
)

const EditUser: React.FC = () => {
  const { getHeaders }              = useContext(AuthContext) as AuthCtx
  const { t }                       = useTranslation()
  const { id }                      = useParams<{ id: string }>()

  const [loaded, setLoaded]         = useState(false)
  const [loading, setLoading]       = useState(false)
  const [formValues, setFormValues] = useState<FormValues | null>(null)
  const [roles, setRoles]           = useState<Role[]>([])
  const [partners, setPartners]     = useState<PartnerOpt[]>([])
  const [marketings, setMarketings] = useState<MktOpt[]>([])

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const headers = await getHeaders()
        const [userRes, partnersRes, marketingsRes] = await Promise.all([
          axios.get<{ message: UserData; roles: Role[] }>(`${url}users/show/${id}`, { headers }),
          axios.get<{ message: PartnerOpt[] }>(`${url}partners`, { headers }),
          axios.get<{ message: MktOpt[] }>(`${url}marketings/all`, { headers }),
        ])
        const u = userRes.data.message
        setRoles(userRes.data.roles)
        setPartners(partnersRes.data.message)
        setMarketings(marketingsRes.data.message)
        setFormValues({
          email          : u.email,
          password       : '',
          passwordConfirm: '',
          role           : u.Roles?.[0]?.id ?? 0,
          partner        : u.PartnerId ?? 0,
          marketing      : u.MarketingId ?? 0,
        })
      } catch (err: any) {
        Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
      } finally {
        setLoaded(true)
      }
    }
    fetchAll()
  }, [])

  const handleSubmit = async (values: FormValues) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.put<{ response: string }>(
        `${url}users/update/${id}`,
        values,
        { headers }
      )
      Swal.fire({ icon: 'success', title: res.data.response })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  if (!loaded) return <CustomLoader />

  return (
    <div className="row justify-content-center">
      <div className="col-md-8 col-lg-6">

        {/* ── Header card ───────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
          marginBottom    : '1.5rem',
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width           : 40,
              height          : 40,
              borderRadius    : 10,
              backgroundColor : `${BRAND}18`,
              display         : 'flex',
              alignItems      : 'center',
              justifyContent  : 'center',
              flexShrink      : 0,
            }}>
              <FontAwesomeIcon icon={faUserPen} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('users.user_edit')}
              </p>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                {formValues?.email}
              </p>
            </div>
          </div>
        </div>

        {/* ── Form card ─────────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
        }}>
          <div style={{ height: 3, backgroundColor: BRAND }} />
          <div style={{ padding: '1.5rem' }}>
            {formValues && (
              <Formik
                initialValues={formValues}
                validationSchema={formSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ values }) => {
                  const role        = parseInt(String(values.role))
                  const showPartner   = role === 2
                  const showMarketing = role === 4

                  return (
                    <Form>

                      {/* Email */}
                      <div className="mb-4">
                        <FieldLabel>{t('login.email')}</FieldLabel>
                        <Field type="email" name="email" className="form-control form-control-sm" />
                        <ErrorMessage name="email" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>

                      {/* Password */}
                      <div className="mb-4">
                        <FieldLabel>{t('login.password')}</FieldLabel>
                        <Field type="password" name="password" className="form-control form-control-sm" placeholder="Leave blank to keep current" />
                        <ErrorMessage name="password" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>

                      {/* Confirm password */}
                      <div className="mb-4">
                        <FieldLabel>{t('login.pass_confirm')}</FieldLabel>
                        <Field type="password" name="passwordConfirm" className="form-control form-control-sm" />
                        <ErrorMessage name="passwordConfirm" component="p" className="text-warning small fw-bold mt-1 mb-0" />
                      </div>

                      {/* Role */}
                      <div className="mb-4">
                        <FieldLabel>Rol</FieldLabel>
                        <Field as="select" name="role" className="form-select form-select-sm text-capitalize">
                          <option value={0} disabled>{t('users.create.select_an_option')}</option>
                          {roles.map((r, i) => (
                            <option key={i} value={r.id} className="text-capitalize">{t(`users.${r.name}`)}</option>
                          ))}
                        </Field>
                        <ErrorMessage name="role" component="p" className="text-warning small fw-bold mt-1 mb-0" />
                      </div>

                      {/* Partner (role = partner) */}
                      {showPartner && (
                        <div className="mb-4">
                          <FieldLabel>{t('users.partner')}</FieldLabel>
                          <Field as="select" name="partner" className="form-select form-select-sm text-capitalize">
                            <option value={0} disabled>{t('users.create.select_an_option')}</option>
                            {partners.map((p, i) => (
                              <option key={i} value={p.id} className="text-capitalize">{p.name}</option>
                            ))}
                          </Field>
                          <ErrorMessage name="partner" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                      )}

                      {/* Marketing */}
                      {showMarketing && (
                        <div className="mb-4">
                          <FieldLabel>{t('users.marketing')}</FieldLabel>
                          <Field as="select" name="marketing" className="form-select form-select-sm">
                            <option value={0} disabled>{t('users.create.select_an_option')}</option>
                            {marketings.map((m, i) => (
                              <option key={i} value={m.id} className="text-capitalize">{m.name}</option>
                            ))}
                          </Field>
                          <ErrorMessage name="marketing" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                        </div>
                      )}

                      {/* Submit */}
                      <button
                        type="submit"
                        disabled={loading}
                        style={{
                          display        : 'inline-flex',
                          alignItems     : 'center',
                          justifyContent : 'center',
                          gap            : '0.5rem',
                          width          : '100%',
                          padding        : '0.55rem 1.5rem',
                          borderRadius   : 9999,
                          backgroundColor: loading ? '#94a3b8' : BRAND,
                          color          : '#fff',
                          fontWeight     : 700,
                          fontSize       : '0.875rem',
                          border         : 'none',
                          cursor         : loading ? 'not-allowed' : 'pointer',
                          boxShadow      : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                          transition     : 'filter 0.15s, transform 0.15s',
                        }}
                        onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }}}
                        onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                      >
                        {loading
                          ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          : <FontAwesomeIcon icon={faSave} />
                        }
                        <span>{t('users.update')}</span>
                      </button>

                    </Form>
                  )
                }}
              </Formik>
            )}
          </div>
        </div>

      </div>
    </div>
  )
}

export default EditUser
